import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { categoryService } from "../../../../services/api/categoryService";
import { proceduceService } from "../../../../services/api/procedureService";
import Loading from "../../components/Loading";
import ProcedureFormPopup from "./ProcedureFormPopup";

const isHtmlString = (val) => {
  if (!val || typeof val !== "string") return false;
  return val.trim().startsWith("<");
};

const isHttpUrl = (val) => {
  if (!val || typeof val !== "string") return false;
  return /^https?:\/\//i.test(val);
};

const ProcedureDetail = () => {
  const { id } = useParams();
  const { token } = useContext(AuthContext);

  // Loading cho phần view detail
  const [loading, setLoading] = useState(false);

  // Dữ liệu hiển thị chi tiết
  const [data, setData] = useState({ name: "", txtUrl: "", fileUrl: "" });
  const [fileContent, setFileContent] = useState("");

  // Edit popup & prefetch
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [prefetchingEdit, setPrefetchingEdit] = useState(false);
  const [editInitialData, setEditInitialData] = useState(null); // { _id, name, fileUrl, submissionTime, reviewTime, category, fileContent }
  const [editCategories, setEditCategories] = useState([]); // [{value,label}]

  // ----- FETCH CHI TIẾT ĐỂ VIEW -----
  const fetchProcedure = useCallback(async () => {
    try {
      setLoading(true);
      const response = await proceduceService.get_by_id(id, token);
      const d = response?.data || {};
      setData(d);

      if (d.txtUrl) {
        if (isHtmlString(d.txtUrl)) {
          // txtUrl đã là HTML string
          setFileContent(d.txtUrl);
        } else if (isHttpUrl(d.txtUrl)) {
          // txtUrl là URL -> fetch
          try {
            const res = await fetch(d.txtUrl);
            const txt = await res.text();
            setFileContent(txt);
          } catch (e) {
            console.warn("Không đọc được txtUrl:", e);
            setFileContent("");
          }
        } else {
          setFileContent("");
        }
      } else {
        setFileContent("");
      }
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response?.data?.message ||
          "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchProcedure();
  }, [fetchProcedure]);

  // ----- PREFETCH DỮ LIỆU EDIT, SAU ĐÓ MỚI MỞ POPUP -----
  const prefetchEditData = useCallback(async () => {
    try {
      setPrefetchingEdit(true);

      // 1) Fetch song song: detail + categories
      const [detailRes, catRes] = await Promise.all([
        proceduceService.get_by_id(id, token),
        categoryService.get("", token),
      ]);

      const detail = detailRes?.data || {};
      const categories = Array.isArray(catRes?.categories)
        ? catRes.categories.map((c) => ({
            value: c.id, // hoặc c._id tùy BE
            label: c.name,
          }))
        : [];

      // 2) Lấy txt content nếu có
      let txt = "";

      if (detail.txtUrl) {
        if (isHtmlString(detail.txtUrl)) {
          txt = detail.txtUrl;
        } else if (isHttpUrl(detail.txtUrl)) {
          try {
            const r = await fetch(detail.txtUrl);
            txt = await r.text();
          } catch {
            txt = "";
          }
        }
      }

      // 3) Map category object cho select
      const catValue = detail?.categoryId?._id || detail?.categoryId || "";
      const catObj = catValue
        ? {
            value: catValue,
            label: categories.find((x) => x.value === catValue)?.label || "",
          }
        : null;

      // 4) Set state cho popup
      setEditCategories(categories);
      setEditInitialData({
        _id: detail._id,
        name: detail.name || "",
        fileUrl: detail.fileUrl || "",
        submissionTime: detail.submissionTime || "",
        reviewTime: detail.reviewTime || "",
        category: catObj,
        fileContent: txt,
      });

      // 5) Mở popup khi mọi thứ đã sẵn sàng
      setIsEditPopupOpen(true);
    } catch (e) {
      toast.dismiss();
      toast.error(
        e?.response?.data?.message || "Không thể tải dữ liệu chỉnh sửa.",
      );
      console.error("Prefetch Edit Error:", e);
    } finally {
      setPrefetchingEdit(false);
    }
  }, [id, token]);

  // ----- TIỆN ÍCH HIỂN THỊ -----
  const beautifyFileName = (originalName) => {
    return (originalName || "").replace(/%20/g, " ").replace(/\s+/g, "_");
  };

  const download = (url) => {
    if (!url) {
      toast.error("Không có file để tải.");
      return;
    }
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", url.split("/").pop());
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ----- OPEN/CLOSE POPUP -----
  const closeEditPopup = () => setIsEditPopupOpen(false);
  const showEditPopup = () => setIsEditPopupOpen(true);

  // ----- RENDER -----
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <div className="bg-white rounded-lg p-6 border-b h-full">
        <h1 className="text-center font-sf-semibold text-[#0457C2] text-2xl uppercase border-b p-4">
          {data.name}
        </h1>

        <div className="flex flex-col gap-6 px-[5vw] h-[calc(100%-107px)] overflow-auto py-6">
          <div className="flex gap-4 flex-wrap">
            <div className="flex gap-2">
              <p className="font-sf-semibold">Loại dịch vụ:</p>
              <p>{data.categoryId?.categoryName || "--"}</p>
            </div>
            {data.submissionTime && (
              <div className="flex gap-2">
                <p className="font-sf-semibold">Thời gian nộp hồ sơ:</p>
                <p>{data.submissionTime}</p>
              </div>
            )}
            {data.reviewTime && (
              <div className="flex gap-2">
                <p className="font-sf-semibold">Thời gian thẩm định:</p>
                <p>{data.reviewTime}</p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <p className="font-sf-semibold">Nội dung:</p>
            {fileContent && (
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: fileContent }}
              />
            )}
          </div>

          {data.fileUrl && (
            <div className="flex flex-col gap-3">
              <p className="font-sf-semibold">File thủ tục:</p>
              <div className="flex gap-2 items-center">
                <div
                  className="w-full bg-white cursor-pointer rounded-lg flex items-center justify-between px-5 py-[13.5px] border border-[#EAEAEA] hover:bg-[#f9f9f9]"
                  onClick={() => download(data.fileUrl)}
                >
                  <div className="flex gap-2 items-center">
                    <img
                      src="/images/document.png"
                      alt="document icon"
                      className="w-5 h-5"
                    />
                    <p className="text-gray-700 text-sm font-sf-medium uppercase">
                      {beautifyFileName((data.fileUrl || "").split("/").pop())}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="w-full flex justify-end">
          <button
            onClick={showEditPopup}
            disabled={prefetchingEdit}
            className={`flex items-center justify-center border bg-blue-500 border-blue-500 text-white rounded-lg py-2 px-[10px] gap-[5px] ${
              prefetchingEdit
                ? "opacity-60 cursor-default"
                : "hover:bg-blue-600"
            }`}
            title={
              prefetchingEdit
                ? "Đang chuẩn bị dữ liệu chỉnh sửa..."
                : "Chỉnh sửa thủ tục"
            }
          >
            <img src="/icons/edit.svg" alt="Edit icon" />
            {prefetchingEdit ? "Đang chuẩn bị..." : "Chỉnh sửa thủ tục"}
          </button>
        </div>

        {/* Edit Popup (mở khi đã có initialData & categories) */}
        {isEditPopupOpen && (
          <ProcedureFormPopup
            mode="edit"
            procedureId={id}
            closePopup={closeEditPopup}
            onSuccess={fetchProcedure}
          />
        )}
      </div>
    </div>
  );
};

export default ProcedureDetail;
